import { Button } from "@dropbox/dig-components/buttons";
import { Box, Stack } from "@dropbox/dig-foundations";
import { SuggestionTile } from "@mirage/mosaics/SuggestionTile/SuggestionTile";
import i18n from "@mirage/translations";
// Note: doing this since it seems like other mosaics allow imports from service-conversation already
// eslint-disable-next-line boundaries/element-types
import { Card, ThemeFamilyKey } from "@dropbox/dash-component-library";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_ChatSuggestion } from "@mirage/analytics/events/types/click_chat_suggestion";
import { PAP_Click_StackChat } from "@mirage/analytics/events/types/click_stack_chat";
import { PAP_Dismiss_DashTooltip } from "@mirage/analytics/events/types/dismiss_dash_tooltip";
import { PAP_Shown_DashTooltip } from "@mirage/analytics/events/types/shown_dash_tooltip";
import {
  type AskWithPromptMultiDocsParams,
  CtaSource,
  type SummarizeItemParams,
} from "@mirage/service-conversation/router_state_types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import type { QuerySuggestionResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import { useStacksOnboardingState } from "@mirage/service-onboarding/hooks";
import { useTheme } from "@mirage/service-settings/theming/useTheme";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { useReportStackPapEvent } from "@mirage/stacks/hooks/useReportStackPapEvent";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStackChatSources } from "../hooks";

import type { StackItemShortcutWithMetadata } from "@mirage/service-stacks/service/types";
import styles from "./StackQuerySuggestions.module.css";
import { useStackQuerySuggestions } from "./useStackQuerySuggestions";

const DEFAULT_QUERY_SUGGESTION = {
  uuid: "stack_query_suggestions_default_query",
  text: i18n.t("summarize_this_stack"),
};

export const StackQuerySuggestions = ({
  stackId,
  summarizableItems,
}: {
  stackId?: string;
  summarizableItems: StackItemShortcutWithMetadata[];
}) => {
  const navigate = useNavigate();
  const { reportPapEvent } = useMirageAnalyticsContext();
  const reportStackPapEvent = useReportStackPapEvent();
  const { chatSources } = useStackChatSources();
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isDarkMode = useTheme();
  const documentIds = useMemo(() => {
    return summarizableItems.reduce((acc, item) => {
      if (item.uuid) {
        acc.push(item.uuid);
      }

      return acc;
    }, [] as string[]);
  }, [summarizableItems]);

  const { isLoading, querySuggestions } = useStackQuerySuggestions(documentIds);
  const { onboardingState, updateOnboardingState } = useStacksOnboardingState();
  const dynamicSuggestions = useMemo(() => {
    return querySuggestions.filter(
      (suggestion) => !suggestion.isStaticSuggestion,
    );
  }, [querySuggestions]);

  useEffect(() => {
    if (
      onboardingState === undefined ||
      onboardingState.querySuggestionsTooltipDismissed ||
      isLoading
    ) {
      return;
    }
    setIsTooltipOpen(true);
    reportPapEvent(
      PAP_Shown_DashTooltip({
        actionSurfaceComponent: "sidebar",
        dashActionSurface: "stack_details_page",
        featureLine: "stacks",
        tooltipName: "stack_query_suggestions",
      }),
    );
  }, [
    isLoading,
    setIsTooltipOpen,
    onboardingState,
    onboardingState?.querySuggestionsTooltipDismissed,
  ]);

  async function closeTooltip(dismissed: boolean = false) {
    setIsTooltipOpen(false);

    if (dismissed) {
      reportPapEvent(
        PAP_Dismiss_DashTooltip({
          actionSurfaceComponent: "sidebar",
          dashActionSurface: "stack_details_page",
          featureLine: "stacks",
          tooltipName: "stack_query_suggestions",
        }),
      );
    }

    updateOnboardingState({
      querySuggestionsTooltipDismissed: true,
    });
  }

  function handleSuggestionClick(_suggestion: QuerySuggestionResult) {
    if (!stackId) return;
    closeTooltip();
    if (_suggestion === DEFAULT_QUERY_SUGGESTION) {
      reportStackPapEvent(
        PAP_Click_StackChat({
          actionSurfaceComponent: "sidebar",
        }),
      );

      // If the default suggestion is clicked, we want to navigate to chat without a pre-built message
      const state = buildConversationRouterState(CtaSource.StackSummarize, {
        chatSources,
        scenarioMeta: {
          type: "stack",
          sharingId: stackId,
        },
      } satisfies SummarizeItemParams);

      navigate(RoutePath.CHAT, {
        state,
      });
    } else {
      reportPapEvent(
        PAP_Click_ChatSuggestion({
          actionSurfaceComponent: "sidebar",
          dashActionSurface: "stack_details_page",
          featureLine: "stacks",
          predictionId: _suggestion.predictionId,
          promptType: "chat_suggestion",
        }),
      );

      // Otherwise, if the user chose a generated suggestion, navigate to chat with the suggestion text
      const state = buildConversationRouterState(
        CtaSource.StackSuggestedPrompt,
        {
          chatSources,
          message: _suggestion.text,
          scenarioMeta: {
            type: "stack",
            sharingId: stackId,
          },
        } satisfies AskWithPromptMultiDocsParams,
      );

      navigate(RoutePath.CHAT, {
        state,
      });
    }
  }

  return (
    <>
      <DigTooltip.Control
        isPortaled
        variant="rich"
        open={isTooltipOpen}
        placement="left"
        triggerRef={triggerRef}
      >
        <DigTooltip.Title>
          {i18n.t("stack_query_suggestions_tooltip_title")}
        </DigTooltip.Title>
        <DigTooltip.Text>
          {i18n.t("stack_query_suggestions_tooltip_text")}
        </DigTooltip.Text>
        <DigTooltip.Actions>
          <Button variant="outline" onClick={() => closeTooltip(true)}>
            {i18n.t("acknowledge_button_cta")}
          </Button>
        </DigTooltip.Actions>
      </DigTooltip.Control>
      <div ref={triggerRef}>
        <Card
          theme={
            isDarkMode
              ? undefined
              : { familyKey: ThemeFamilyKey.Cloud, variant: "subtle" }
          }
          loading={isLoading}
          style={{
            // @ts-expect-error -- Custom CSS variable for loader animation color
            // TODO: expose this color as a prop in the component, instead
            "--loader-animation-color": "var(--dig-color__primary__base)",
            "--card-background": "var(--dig-color__selection__surface)",
          }}
        >
          <Card.Header
            title={i18n.t("stack_query_suggestions_title")}
            withMargin={true}
            className={styles.header}
          />
          <Card.Contents
            as="ul"
            listStyleType="none"
            marginY="0"
            padding="0"
            breakout="standard"
          >
            <Stack gap="Micro Small">
              {dynamicSuggestions.map((suggestion) => (
                <SuggestionTile
                  key={suggestion.uuid}
                  size="small"
                  text={suggestion.text}
                  onClick={() => handleSuggestionClick(suggestion)}
                  isLoading={isLoading}
                  display="block"
                  tileStyles={{
                    borderRadius: "var(--dash-radius__component)",
                  }}
                  predictionId={suggestion.predictionId}
                />
              ))}
              <DigTooltip
                isPortaled
                openDelay={600}
                placement="top"
                title={i18n.t("summarize_this_stack_tooltip", {
                  count: summarizableItems.length ?? 0,
                })}
              >
                <Box>
                  <SuggestionTile
                    size="small"
                    text={DEFAULT_QUERY_SUGGESTION.text}
                    onClick={() =>
                      handleSuggestionClick(DEFAULT_QUERY_SUGGESTION)
                    }
                    isLoading={isLoading}
                    display="block"
                    tileStyles={{
                      borderRadius: "var(--dash-radius__component)",
                    }}
                  />
                </Box>
              </DigTooltip>
            </Stack>
          </Card.Contents>
        </Card>
      </div>
    </>
  );
};
